// Marks a rejection as originating from our managed abort controllers so the
// global unhandledrejection handler below can suppress it without silencing
// unrelated errors.
const MANAGED_ABORT = Symbol.for('silverfin:managed-abort')

type ManagedAbortError = DOMException & { [MANAGED_ABORT]?: true }

export const createAbortReason = (reason: string): ManagedAbortError => {
  const error = new DOMException(reason, 'AbortError') as ManagedAbortError
  error[MANAGED_ABORT] = true
  // Give the DOMException a stack trace for dev overlays and debugging.
  try {
    error.stack = new Error(reason).stack
  } catch {
    /* ignore */
  }
  return error
}

// Aborting a shared AbortController rejects every promise chained to that
// signal — main fetch, body-stream reads, and any follow-up awaits that hadn't
// settled yet. The code that kicked off the request catches its own rejection,
// but sibling rejections (e.g. a body-reader promise) can still surface as
// `unhandledrejection` events. Those aren't bugs — they're inherent to the
// abort flow — so we suppress them here at a single choke-point.
//
// Registering in the capture phase so we run before downstream listeners
// (notably webpack-dev-server's runtime-error overlay in development, which
// otherwise pops up on every cancelled fetch during rapid UI interactions).
if (typeof window !== 'undefined') {
  window.addEventListener(
    'unhandledrejection',
    (event) => {
      const reason = event.reason as ManagedAbortError | null | undefined
      if (reason && reason[MANAGED_ABORT]) {
        event.preventDefault()
        event.stopImmediatePropagation()
      }
    },
    { capture: true }
  )
}

const abortControllers = {
  typeahead: new AbortController(),
  communications: new AbortController(),
  asyncRender: new AbortController(),
  insightsCompaniesOverview: new AbortController(),
  leftDrawer: new AbortController(),
  usersList: new AbortController(),
  userPanel: new AbortController(),
  groupsList: new AbortController(),
}

/**
 * Centrally manage abort controllers. Provide a key to abort a class of requests and async actions.
 * @param key - The key of the abort controller to abort.
 * @param reason - The reason for aborting.
 */
export const abort = (
  key: keyof typeof abortControllers,
  reason: string = 'Aborted'
): void => {
  if (!abortControllers[key]) {
    return
  }

  abortControllers[key].abort(createAbortReason(reason))
  abortControllers[key] = new AbortController()
}

/**
 * Get the signal of an abort controller for a specific class of requests and async actions.
 * @param key - The key of the abort controller to get the signal from.
 */
export const getSignal = (
  key: keyof typeof abortControllers
): AbortSignal | undefined => {
  if (!abortControllers[key]) {
    return
  }

  return abortControllers[key].signal
}
